import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Car, 
  ShoppingCart, 
  TrendingUp, 
  Package,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const EmployeeDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_vehicles: 0,
    total_orders: 0,
    vehicles_by_status: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'purchased':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'purchased':
        return <Package className="h-4 w-4" />;
      case 'shipped':
        return <Clock className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-slate-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="employee-dashboard">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome, {user.full_name}!</h1>
        <p className="text-emerald-100">Manage vehicles, orders, and track business progress.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Vehicles in Inventory</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-vehicles">
                  {stats.total_vehicles}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Car className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Active Orders</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-orders">
                  {stats.total_orders}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Monthly Performance</p>
                <p className="text-2xl font-bold text-slate-900">+8.2%</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicle Status and Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Vehicle Status Overview</CardTitle>
            <CardDescription>Current status distribution of vehicles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.vehicles_by_status.length > 0 ? (
                stats.vehicles_by_status.map((statusData) => (
                  <div key={statusData._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(statusData._id)}
                      <div>
                        <Badge className={getStatusColor(statusData._id)}>
                          {statusData._id?.charAt(0).toUpperCase() + statusData._id?.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-slate-900">{statusData.count}</p>
                      <p className="text-sm text-slate-500">vehicles</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No vehicles in inventory</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Today's Tasks</CardTitle>
            <CardDescription>Important tasks for today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">Update shipping status</p>
                  <p className="text-xs text-yellow-700">Check for vehicles ready to ship</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Package className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Process new arrivals</p>
                  <p className="text-xs text-blue-700">Add recently purchased vehicles</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">Customer follow-up</p>
                  <p className="text-xs text-green-700">Contact clients about delivery status</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Frequently used functions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="p-4 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <Car className="h-8 w-8 text-blue-600 mb-2" />
              <h3 className="font-medium text-slate-900">Add Vehicle</h3>
              <p className="text-sm text-slate-500">New inventory entry</p>
            </button>
            <button className="p-4 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <ShoppingCart className="h-8 w-8 text-emerald-600 mb-2" />
              <h3 className="font-medium text-slate-900">Create Order</h3>
              <p className="text-sm text-slate-500">Process customer order</p>
            </button>
            <button className="p-4 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <Package className="h-8 w-8 text-orange-600 mb-2" />
              <h3 className="font-medium text-slate-900">Update Status</h3>
              <p className="text-sm text-slate-500">Change vehicle status</p>
            </button>
            <button className="p-4 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <TrendingUp className="h-8 w-8 text-purple-600 mb-2" />
              <h3 className="font-medium text-slate-900">View Reports</h3>
              <p className="text-sm text-slate-500">Business analytics</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;